import * as React from "react";
import { styled, useTheme, useColorScheme } from "@mui/material/styles";
import Avatar from "@mui/material/Avatar";
import MuiDrawer, { drawerClasses } from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import { useTranslation } from "react-i18next";
import MenuContent from "./MenuContent";
import OptionsMenu from "./OptionsMenu";
import { getCurrentTenant } from "../config/tenantConfig";

const drawerWidth = 240;

const Drawer = styled(MuiDrawer)({
  width: drawerWidth,
  flexShrink: 0,
  boxSizing: "border-box",
  mt: 10,
  [`& .${drawerClasses.paper}`]: {
    width: drawerWidth,
    boxSizing: "border-box",
  },
});

export default function SideMenu() {
  const theme = useTheme();
  const { mode, setMode } = useColorScheme();
  const { t } = useTranslation();
  const toggleMode = () => {
    // Geçici olarak dark/light mode geçişi kapatıldı, sadece light mod
    // if (!mode) return;
    // setMode(mode === "light" ? "dark" : "light");
  };
  const tenant = getCurrentTenant();
  const logoLight = tenant?.theme?.logoLight ?? tenant?.logoLight;
  const logoDark = tenant?.theme?.logoDark ?? tenant?.logoDark;
  const logoSrc =
    (theme.palette.mode === "dark" ? logoDark : logoLight) || null;
  const userName = localStorage.getItem("userName");
  const displayName = userName
    ? userName.split("@")[0]
    : localStorage.getItem("userName");
  const companyName = localStorage.getItem("companyName");
  const rolesRaw = localStorage.getItem("userRoles");
  let roles = [];
  try {
    if (rolesRaw) {
      const parsed = JSON.parse(rolesRaw);
      if (Array.isArray(parsed)) roles = parsed;
      else if (typeof parsed === "string") roles = [parsed];
    }
  } catch (e) {
    // fallback: try to split plain text like "admin,superAdmin" or '["admin","superAdmin"]'
    roles = (rolesRaw || "")
      .replaceAll("[", "")
      .replaceAll("]", "")
      .replaceAll('"', "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  const formatRole = (r) =>
    r.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (c) => c.toUpperCase());
  // If user has superAdmin among roles, show only superAdmin
  const hasSuperAdmin = roles.some(
    (r) => String(r).toLowerCase() === "superadmin"
  );
  const displayRoles = hasSuperAdmin ? ["superAdmin"] : roles;
  return (
    <Drawer
      variant="permanent"
      sx={{
        display: { xs: "none", md: "block" },
        [`& .${drawerClasses.paper}`]: {
          backgroundColor: "background.paper",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          // justifyContent: 'center',
          mt: "calc(var(--template-frame-height, 0px) + 4px)",
          p: 3,
        }}
      >
        {logoSrc ? (
          <Box
            component="img"
            src={logoSrc}
            alt={`${tenant?.name ?? "App"} Logo`}
            sx={{
              height: 44,
              width: "auto",
              display: "block",
              objectFit: "contain",
              userSelect: "none",
              cursor: "pointer",
              transition: "opacity 0.2s",
              "&:hover": { opacity: 0.8 },
            }}
            onClick={toggleMode}
          />
        ) : (
          <Typography
            onClick={toggleMode}
            sx={{
              cursor: "pointer",
              transition: "opacity 0.2s",
              "&:hover": { opacity: 0.8 },
              ...(tenant?.textStyle || {}),
            }}
          >
            {tenant?.name || "App"}
          </Typography>
        )}
      </Box>
      <Divider />
      <Box
        sx={{
          overflow: "auto",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <MenuContent />
      </Box>

      <Stack
        direction="row"
        sx={{
          p: 2,
          gap: 1,
          alignItems: "center",
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Avatar
          sizes="small"
          alt={displayName}
          // src="/static/images/avatar/7.jpg"
          sx={{ width: 36, height: 36 }}
        />
        <Box sx={{ mr: "auto" }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, lineHeight: "16px" }}
          >
            {displayName}
          </Typography>
        </Box>
        <OptionsMenu />
      </Stack>
      <Stack
        direction="row"
        sx={{
          p: 1.1,
          alignItems: "center",
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            divider={<Divider orientation="vertical" flexItem />}
          >
            {companyName && (
              <Typography
                variant="body3"
                sx={{ fontWeight: 500, lineHeight: "16px" }}
              >
                {companyName}
              </Typography>
            )}
            {displayRoles.length > 0 && (
              <Typography
                variant="body3"
                sx={{ fontWeight: 500, lineHeight: "16px" }}
              >
                {displayRoles.map(formatRole).join(", ")}
              </Typography>
            )}
          </Stack>
        </Box>
      </Stack>
    </Drawer>
  );
}
